import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Chip,
  CircularProgress,
  Typography
} from '@mui/material';

import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import SummaryTiles, { TILE_ACCENT } from '../../../../components/utils/SummaryTiles';
import TopBar from '../../../../components/utils/TopBar';

import {
  GetIdentityOverviewBIZidentity
} from '../../../../redux/actions/business/identity/identityOverviewActions';

const SECTION_LABEL_COLOR = 'rgba(226, 242, 249, 0.6)';

const Section = ({ title, hint, children }) => (
  <Box mb={"22px"}>
    <Typography variant='subtitle2' sx={{ mb: hint ? "2px" : "10px" }}>
      {title}
    </Typography>
    {
      hint
      &&
      <Typography variant='caption' sx={{ color: SECTION_LABEL_COLOR, display: "block", mb: "10px" }}>
        {hint}
      </Typography>
    }
    {children}
  </Box>
);

const HomePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const overview = useSelector(state => state["business.identity"]?.identityOverview);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    handleGetOverview();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleGetOverview() {
    await dispatch(GetIdentityOverviewBIZidentity(setLoading));
  }

  const directory = overview?.directory;
  const admin = overview?.admin;
  const customer = overview?.customer;
  const activity = overview?.activity_7d;
  const integrity = overview?.integrity;
  const breakdowns = overview?.breakdowns;

  // Only genuinely actionable rows appear here, and only when the count is
  // non-zero — a panel full of green zeroes is noise.
  const attention = [
    {
      count: admin?.mfa_missing,
      one: "internal user has no MFA set up",
      many: "internal users have no MFA set up",
      to: "/business/identity/internal-users",
    },
    {
      count: integrity?.companies_without_admin,
      one: "company has an admin user that no longer resolves",
      many: "companies have an admin user that no longer resolves",
      to: "/business/identity/companies",
    },
    {
      count: integrity?.companies_without_tenant,
      one: "company has a tenant that no longer resolves",
      many: "companies have a tenant that no longer resolves",
      to: "/business/identity/companies",
    },
    {
      count: integrity?.users_without_company,
      one: "user is not linked to any company",
      many: "users are not linked to any company",
      to: "/business/identity/company-employees",
    },
    {
      count: admin?.login_disabled,
      one: "internal user has login disabled",
      many: "internal users have login disabled",
      to: "/business/identity/internal-users",
    },
  ]
    .filter((item) => Number(item.count) > 0)
    .map((item) => ({
      ...item,
      label: Number(item.count) === 1 ? item.one : item.many,
    }));

  const directoryTiles = [
    { label: "Tenants", value: directory?.tenants ?? 0, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/identity/tenants") },
    { label: "Companies", value: directory?.companies ?? 0, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/identity/companies") },
    { label: "Branches", value: directory?.branches ?? 0, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/identity/branches") },
    { label: "Users", value: directory?.users ?? 0, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/identity/users") },
    { label: "Company employees", value: directory?.company_employees ?? 0, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/identity/company-employees") },
  ];

  const adminTiles = [
    { label: "Internal users", value: admin?.internal_users ?? 0, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/identity/internal-users") },
    { label: "MFA enabled", value: admin?.mfa_enabled ?? 0, accent: TILE_ACCENT.good, onClick: () => navigate("/business/identity/internal-users-mfa") },
    { label: "Without MFA", value: admin?.mfa_missing ?? 0, accent: Number(admin?.mfa_missing) > 0 ? TILE_ACCENT.bad : TILE_ACCENT.good, onClick: () => navigate("/business/identity/internal-users") },
    { label: "Login disabled", value: admin?.login_disabled ?? 0, accent: Number(admin?.login_disabled) > 0 ? TILE_ACCENT.warn : TILE_ACCENT.good, onClick: () => navigate("/business/identity/internal-users") },
    { label: "Active sessions", value: admin?.sessions_active ?? 0, accent: TILE_ACCENT.info, onClick: () => navigate("/business/identity/internal-user-sessions") },
  ];

  const customerTiles = [
    { label: "Active sessions", value: customer?.sessions_active ?? 0, accent: TILE_ACCENT.info, onClick: () => navigate("/business/identity/user-sessions") },
    { label: "Login disabled", value: customer?.login_disabled ?? 0, accent: Number(customer?.login_disabled) > 0 ? TILE_ACCENT.warn : TILE_ACCENT.good, onClick: () => navigate("/business/identity/users") },
    { label: "Email unverified", value: customer?.email_unverified ?? 0, accent: Number(customer?.email_unverified) > 0 ? TILE_ACCENT.warn : TILE_ACCENT.good, onClick: () => navigate("/business/identity/users") },
  ];

  const activityTiles = [
    { label: "New users", value: activity?.new_users ?? 0, accent: TILE_ACCENT.neutral },
    { label: "New companies", value: activity?.new_companies ?? 0, accent: TILE_ACCENT.neutral },
    { label: "Sessions started", value: activity?.sessions_started ?? 0, accent: TILE_ACCENT.neutral },
  ];

  // A breakdown with a single value says nothing the totals above don't, so it
  // is only worth rendering once the column actually varies.
  const employeeStatus = breakdowns?.employee_status || [];
  const accountStatus = breakdowns?.account_status || [];
  const showEmployeeStatus = employeeStatus.length > 1;
  const showAccountStatus = accountStatus.length > 1;

  return (
    <Box>

      <TopBar
        title={"Identity - MS"}
        button1Text={"Refresh"}
        button1OnClick={() => {
          handleGetOverview();
        }}
        />

      <Typography
        variant='caption'
        sx={{ color: SECTION_LABEL_COLOR, display: "block", mb: "20px" }}>
        Live counts from the identity service. Every tile links to the screen it
        came from. Counts exclude soft-deleted rows.
      </Typography>

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
        !overview
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <Typography>Overview unavailable</Typography>
        </Box>
        :
        <Box>

          {/* ---------- needs attention ---------- */}
          <Section title={"Needs attention"}>
            {
              attention.length === 0
              ?
              <Box
                className="flexCenter_Row"
                gap={"10px"}
                sx={{
                  border: `1px solid ${TILE_ACCENT.good}40`,
                  background: `${TILE_ACCENT.good}14`,
                  borderRadius: "12px",
                  padding: "14px 16px",
                }}>
                <CheckCircleOutlineOutlinedIcon sx={{ color: TILE_ACCENT.good }} />
                <Typography variant='body2'>
                  Nothing to action — no MFA gaps, disabled logins or unresolved references.
                </Typography>
              </Box>
              :
              <Box display={"flex"} flexDirection={"column"} gap={"8px"}>
                {
                  attention.map((item, index) => (
                    <Box
                      key={index}
                      className="flexCenterSBRow"
                      onClick={() => navigate(item.to)}
                      sx={{
                        border: `1px solid ${TILE_ACCENT.warn}40`,
                        background: `${TILE_ACCENT.warn}14`,
                        borderRadius: "12px",
                        padding: "12px 16px",
                        cursor: "pointer",
                        transition: "all .15s",
                        "&:hover": {
                          background: `${TILE_ACCENT.warn}24`,
                          borderColor: `${TILE_ACCENT.warn}80`,
                        },
                      }}>
                      <Box className="flexCenter_Row" gap={"12px"}>
                        <ReportProblemOutlinedIcon sx={{ color: TILE_ACCENT.warn }} />
                        <Typography variant='body2'>
                          <b>{item.count}</b>&nbsp;{item.label}
                        </Typography>
                      </Box>
                      <ChevronRightIcon sx={{ color: SECTION_LABEL_COLOR }} />
                    </Box>
                  ))
                }
              </Box>
            }
          </Section>

          {/* ---------- directory ---------- */}
          <Section title={"Directory"}>
            <SummaryTiles tiles={directoryTiles} />
          </Section>

          {/* ---------- admin access ---------- */}
          <Section
            title={"Admin access"}
            hint={"Internal portal accounts — these are the ones with privileged access."}>
            <SummaryTiles tiles={adminTiles} />
          </Section>

          {/* ---------- customer access ---------- */}
          <Section
            title={"Customer access"}
            hint={"End users of the customer app."}>
            <SummaryTiles tiles={customerTiles} />
          </Section>

          {/* ---------- activity ---------- */}
          <Section
            title={"Last 7 days"}
            hint={"A sudden zero here usually means something upstream stopped working."}>
            <SummaryTiles tiles={activityTiles} />
          </Section>

          {/* ---------- breakdowns (only when the column actually varies) ---------- */}
          {
            (showEmployeeStatus || showAccountStatus)
            &&
            <Section title={"Status breakdown"}>
              <Box display={"flex"} flexWrap={"wrap"} gap={"30px"}>
                {
                  showEmployeeStatus
                  &&
                  <Box>
                    <Typography variant='caption' sx={{ color: SECTION_LABEL_COLOR, display: "block", mb: "8px" }}>
                      Company employees
                    </Typography>
                    <Box className="flexCenter_Row" gap={"8px"} flexWrap={"wrap"}>
                      {
                        employeeStatus.map((s, i) => (
                          <Chip key={i} size='small' variant='outlined'
                            label={`${s.status || "unknown"} · ${s.count}`} />
                        ))
                      }
                    </Box>
                  </Box>
                }
                {
                  showAccountStatus
                  &&
                  <Box>
                    <Typography variant='caption' sx={{ color: SECTION_LABEL_COLOR, display: "block", mb: "8px" }}>
                      User accounts
                    </Typography>
                    <Box className="flexCenter_Row" gap={"8px"} flexWrap={"wrap"}>
                      {
                        accountStatus.map((s, i) => (
                          <Chip key={i} size='small' variant='outlined'
                            label={`${s.status || "unknown"} · ${s.count}`} />
                        ))
                      }
                    </Box>
                  </Box>
                }
              </Box>
            </Section>
          }
        </Box>
      }
    </Box>
  )
}

export default HomePage;
